import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../App';
import { useModalManager } from '../hooks/useModalManager';
import useBatchProcessor from '../hooks/useBatchProcessor';
import useBatchLoader from '../hooks/useBatchLoader';
import { useDragToScroll } from '../hooks/useDragToScroll';
import BatchProgressModal from '../components/BatchProgressModal';
import { isPro } from '../Helpers';
import api from '../utils/api';
import LoadingState from '../components/LoadingState';

const BulkProductEditor = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showSuccess, showError } = useToast();
    const { showPremiumModal } = useModalManager();
    const { batchProgress, processBatch, resetProgress, shouldUseBatchProcessing } = useBatchProcessor();
    const { loadingProgress, batchLoadItems, resetProgress: resetLoadingProgress, shouldUseBatchLoading } = useBatchLoader();
    
    const selectedProductIds = location.state?.selectedProducts || [];
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [categories, setCategories] = useState([]);
    const [tags, setTags] = useState([]);
    const [settings, setSettings] = useState({});
    const [showBatchModal, setShowBatchModal] = useState(false);
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [usingBatchLoading, setUsingBatchLoading] = useState(false);
    
    // Check if selection exceeds free limit
    const BULK_EDIT_LIMIT = 100;
    const isOverLimit = !isPro() && selectedProductIds.length > BULK_EDIT_LIMIT;
    
    // Bulk edit mode: 'common' or 'individual'
    const [editMode, setEditMode] = useState('common');
    
    // Common fields to apply to all products
    const [commonFields, setCommonFields] = useState({
        type: '',
        status: '',
        stock_status: '',
        manage_stock: '',
        category_ids: [],
        tag_ids: [],
        regular_price: '',
        sale_price: '',
        tax_status: '',
        tax_class: '',
        weight: '',
        length: '',
        width: '',
        height: '',
        shipping_class: '',
        featured: '',
        short_description: '',
        description: '',
    });
    
    // Toggle states for each field - true means "Edit Together" (common), false means individual
    const [fieldToggles, setFieldToggles] = useState({
        type: true,
        status: true,
        featured: true,
        regular_price: true,
        sale_price: true,
        tax_status: true,
        tax_class: true,
        stock_status: true,
        manage_stock: true,
        weight: true,
        length: true,
        width: true,
        height: true,
        shipping_class: true,
        short_description: false,
        description: false,
        sku: false, // Individual by default
        stock_quantity: false, // Individual by default
        name: false, // Individual by default
    });
    
    // Individual product edits
    const [productEdits, setProductEdits] = useState({});

    // Drag-to-scroll functionality for the table
    const tableScrollRef = useDragToScroll({
        horizontal: true,
        vertical: true
    });

    useEffect(() => {
        if (selectedProductIds.length === 0) {
            showError('No products selected');
            navigate('/products');
            return;
        }
        initializeData();
    }, []);

    const initializeData = async () => {
        try {
            // Load settings first and wait for them
            const loadedSettings = await loadSettings();
            // Then load products with settings available
            await loadData(loadedSettings);
        } catch (error) {
            showError('Failed to initialize bulk editor');
        }
    };

    const loadSettings = async () => {
        try {
            const response = await api.getSettings();
            if (response.success) {
                setSettings(response.settings);
                console.log('📋 Settings loaded:', response.settings);
                return response.settings;
            }
            return {};
        } catch (error) {
            console.error('Failed to load settings:', error);
            return {};
        }
    };

    const loadData = async (loadedSettings = settings) => {
        try {
            setLoading(true);
            
            console.log(`🔍 Loading ${selectedProductIds.length} products...`);
            console.log('📋 Current settings:', loadedSettings);
            
            // Check if we should use batch loading
            const shouldUseBatch = shouldUseBatchLoading(selectedProductIds.length, loadedSettings);
            
            console.log('🤔 Should use batch loading?', {
                itemCount: selectedProductIds.length,
                settings: loadedSettings,
                shouldUseBatch: shouldUseBatch
            });
            
            if (shouldUseBatch) {
                console.log('🚀 Using batch loading - showing progress modal');
                setUsingBatchLoading(true);
                setLoading(false); // Don't show regular loading when using batch loading
                setShowLoadingModal(true);
            }
            
            // Load products using batch loading if needed
            const loadedProducts = await batchLoadItems(selectedProductIds, 'products', 100, loadedSettings);
            
            setProducts(loadedProducts);
            
            // Initialize product edits
            const edits = {};
            loadedProducts.forEach(product => {
                edits[product.id] = {
                    name: product.name,
                    type: product.type,
                    sku: product.sku,
                    regular_price: product.regular_price,
                    sale_price: product.sale_price,
                    stock_quantity: product.stock_quantity,
                    stock_status: product.stock_status,
                    manage_stock: product.manage_stock,
                    status: product.status,
                    weight: product.weight,
                    dimensions: product.dimensions || {},
                    shipping_class: product.shipping_class,
                    featured: product.featured,
                    images: product.images || [],
                    categories: product.categories || [],
                    tags: product.tags || [],
                    short_description: product.short_description || '',
                    description: product.description || '',
                };
            });
            setProductEdits(edits);
            
            // Load categories and tags
            const [categoriesRes, tagsRes] = await Promise.all([
                api.getCategories(),
                api.getTags()
            ]);
            
            if (categoriesRes.success) setCategories(categoriesRes.categories || []);
            if (tagsRes.success) setTags(tagsRes.tags || []);
            
            console.log(`✅ Successfully loaded ${loadedProducts.length} products`);
            
        } catch (error) {
            showError(error.message || 'Failed to load products');
        } finally {
            setLoading(false);
            setShowLoadingModal(false);
            setUsingBatchLoading(false);
        }
    };

    const handleCommonFieldChange = (field, value) => {
        setCommonFields(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleProductFieldChange = (productId, field, value) => {
        setProductEdits(prev => ({
            ...prev,
            [productId]: {
                ...prev[productId],
                [field]: value
            }
        }));
    };

    const applyCommonFields = async () => {
        if (isOverLimit) {
            showPremiumModal();
            return;
        }
        
        try {
            setSaving(true);
            
            // Apply common fields to productEdits first
            const updatedEdits = { ...productEdits };
            let hasChanges = false;
            
            Object.keys(updatedEdits).forEach(productId => {
                Object.keys(commonFields).forEach(field => {
                    // Only apply if field toggle is ON and has a value
                    if (fieldToggles[field] && commonFields[field] !== '' && commonFields[field] !== null && commonFields[field] !== undefined) {
                        if (field === 'length' || field === 'width' || field === 'height') {
                            if (!updatedEdits[productId].dimensions) {
                                updatedEdits[productId].dimensions = {};
                            }
                            updatedEdits[productId].dimensions[field] = commonFields[field];
                        } else {
                            updatedEdits[productId][field] = commonFields[field];
                        }
                        hasChanges = true;
                    }
                });
            });
            
            if (!hasChanges) {
                showError('No fields to apply. Toggle fields ON and set values.');
                setSaving(false);
                return;
            }

            const productIds = Object.keys(updatedEdits);
            const shouldUseBatch = shouldUseBatchProcessing(productIds.length, settings);

            console.log('🔍 Apply Common Fields - Processing Decision:', {
                productCount: productIds.length,
                settings: settings,
                shouldUseBatch: shouldUseBatch,
                batchSize: parseInt(settings.bulk_edit_batch_size) || 5000,
                threshold: parseInt(settings.bulk_edit_threshold) || 1000
            });

            if (shouldUseBatch) {
                // Use batch processing for large datasets
                setShowBatchModal(true);
                
                // Prepare batch data
                const batchData = Object.keys(updatedEdits).map(productId => ({
                    id: productId,
                    data: updatedEdits[productId]
                }));

                await processBatch(
                    productIds,
                    'bulkUpdate',
                    { updates: batchData },
                    parseInt(settings.bulk_edit_batch_size) || 5000,
                    'products'
                );

                showSuccess(`Successfully applied common fields to ${productIds.length} products using batch processing`);
                setProductEdits(updatedEdits);
            } else {
                // Use regular processing for smaller datasets
                const updatePromises = Object.keys(updatedEdits).map(productId => {
                    const updates = updatedEdits[productId];
                    return api.updateProduct(productId, updates);
                });
                
                const results = await Promise.all(updatePromises);
                const successCount = results.filter(r => r.success).length;
                
                if (successCount === results.length) {
                    showSuccess(`Successfully applied common fields to ${successCount} products`);
                    setProductEdits(updatedEdits);
                } else {
                    showError(`Updated ${successCount} of ${results.length} products`);
                }
            }
            
        } catch (error) {
            showError('Failed to apply common fields');
        } finally {
            setSaving(false);
        }
    };
    
    const applyIndividualChanges = async () => {
        if (isOverLimit) {
            showPremiumModal();
            return;
        }
        
        try {
            setSaving(true);

            const productIds = Object.keys(productEdits);
            const shouldUseBatch = shouldUseBatchProcessing(productIds.length, settings);

            console.log('🔍 Apply Individual Changes - Processing Decision:', {
                productCount: productIds.length,
                settings: settings,
                shouldUseBatch: shouldUseBatch,
                batchSize: parseInt(settings.bulk_edit_batch_size) || 5000,
                threshold: parseInt(settings.bulk_edit_threshold) || 1000
            });

            if (shouldUseBatch) {
                // Use batch processing for large datasets
                setShowBatchModal(true);
                
                // Prepare batch data
                const batchData = Object.keys(productEdits).map(productId => ({
                    id: productId,
                    data: productEdits[productId]
                }));

                await processBatch(
                    productIds,
                    'bulkUpdate',
                    { updates: batchData },
                    parseInt(settings.bulk_edit_batch_size) || 5000,
                    'products'
                );

                showSuccess(`Successfully updated ${productIds.length} products with individual changes using batch processing`);
            } else {
                // Use regular processing for smaller datasets
                const updatePromises = Object.keys(productEdits).map(productId => {
                    const updates = productEdits[productId];
                    return api.updateProduct(productId, updates);
                });
                
                const results = await Promise.all(updatePromises);
                const successCount = results.filter(r => r.success).length;
                
                if (successCount === results.length) {
                    showSuccess(`Successfully updated ${successCount} products with individual changes`);
                } else {
                    showError(`Updated ${successCount} of ${results.length} products`);
                }
            }
            
        } catch (error) {
            showError('Failed to save individual changes');
        } finally {
            setSaving(false);
        }
    };

    const toggleField = (field) => {
        setFieldToggles(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleSave = async () => {
        if (isOverLimit) {
            showPremiumModal();
            return;
        }
        
        try {
            setSaving(true);
            
            // First apply common fields to productEdits
            const updatedEdits = { ...productEdits };
            
            Object.keys(updatedEdits).forEach(productId => {
                Object.keys(commonFields).forEach(field => {
                    // Only apply if field toggle is ON and has a value
                    if (fieldToggles[field] && commonFields[field] !== '' && commonFields[field] !== null && commonFields[field] !== undefined) {
                        if (field === 'length' || field === 'width' || field === 'height') {
                            if (!updatedEdits[productId].dimensions) {
                                updatedEdits[productId].dimensions = {};
                            }
                            updatedEdits[productId].dimensions[field] = commonFields[field];
                        } else {
                            updatedEdits[productId][field] = commonFields[field];
                        }
                    }
                });
            });

            const productIds = Object.keys(updatedEdits);
            const shouldUseBatch = shouldUseBatchProcessing(productIds.length, settings);

            console.log('🔍 Bulk Product Editor - Processing Decision:', {
                productCount: productIds.length,
                settings: settings,
                shouldUseBatch: shouldUseBatch,
                batchSize: parseInt(settings.bulk_edit_batch_size) || 5000,
                threshold: parseInt(settings.bulk_edit_threshold) || 1000
            });

            if (shouldUseBatch) {
                // Use batch processing for large datasets
                setShowBatchModal(true);
                
                // Prepare batch data
                const batchData = Object.keys(updatedEdits).map(productId => ({
                    id: productId,
                    data: updatedEdits[productId]
                }));

                await processBatch(
                    productIds,
                    'bulkUpdate',
                    { updates: batchData },
                    parseInt(settings.bulk_edit_batch_size) || 5000,
                    'products'
                );

                showSuccess(`Successfully updated ${productIds.length} products using batch processing`);
                navigate('/products');
            } else {
                // Use regular processing for smaller datasets
                const updatePromises = Object.keys(updatedEdits).map(productId => {
                    const updates = updatedEdits[productId];
                    return api.updateProduct(productId, updates);
                });
                
                const results = await Promise.all(updatePromises);
                const successCount = results.filter(r => r.success).length;
                
                if (successCount === results.length) {
                    showSuccess(`Successfully updated ${successCount} products`);
                    navigate('/products');
                } else {
                    showError(`Updated ${successCount} of ${results.length} products`);
                }
            }
            
        } catch (error) {
            showError(error.message || 'Failed to save products');
        } finally {
            setSaving(false);
        }
    };

    if (loading && !usingBatchLoading) {
        return (
            <div className="page bulk-editor-page">
                <div className="page-container">
                    <LoadingState message="Loading products..." />
                </div>
            </div>
        );
    }

    return (
        <div className="page bulk-editor-page">
            <div className="page-container">
                <div className="page-header">
                    <div className="page-title">
                        <h1>Bulk Edit Products</h1>
                        <p>Editing {products.length} products</p>
                    </div>
                    <div className="page-actions">
                        <button 
                            className="btn btn-secondary"
                            onClick={() => navigate('/products')}
                        >
                            Cancel
                        </button>
                        <button 
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={saving || isOverLimit}
                        >
                            {saving ? 'Saving...' : 'Save All Changes'}
                        </button>
                    </div>
                </div>

                {/* Bulk Edit Limit Notice */}
                {isOverLimit && (
                    <div className="bulk-limit-notice">
                        <div className="limit-notice-content">
                            <div className="notice-icon">⚠️</div>
                            <div className="notice-text">
                                <h3>Selection Limit Exceeded</h3>
                                <p>
                                    You have selected <strong>{selectedProductIds.length} products</strong>. 
                                    The free version allows bulk editing up to <strong>{BULK_EDIT_LIMIT} products</strong> at once.
                                </p>
                                <p>
                                    Please reduce your selection to {BULK_EDIT_LIMIT} or fewer products, or upgrade to Pro for unlimited bulk editing.
                                </p>
                            </div>
                            <div className="notice-actions">
                                <button 
                                    className="btn btn-primary"
                                    onClick={showPremiumModal}
                                >
                                    Upgrade to Pro
                                </button>
                                <button 
                                    className="btn btn-secondary"
                                    onClick={() => navigate('/products')}
                                >
                                    Reduce Selection
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit Mode Tabs */}
                <div className="edit-mode-tabs">
                    <button
                        className={`mode-tab ${editMode === 'common' ? 'active' : ''}`}
                        onClick={() => setEditMode('common')}
                    >
                        📝 Common Fields
                    </button>
                    <button
                        className={`mode-tab ${editMode === 'individual' ? 'active' : ''}`}
                        onClick={() => setEditMode('individual')}
                    >
                        📋 Individual Edit
                    </button>
                </div>

                {/* Common Fields Mode */}
                {editMode === 'common' && (
                    <div className="common-fields-section">
                        <div className="section-header">
                            <h3>Common Fields</h3>
                            <p>Toggle "Edit Together" ON to edit here, or OFF to edit individually in the table</p>
                        </div>

                        <div className="common-fields-grid">
                            {/* Product Type */}
                            <div className={`field-group ${!fieldToggles.type ? 'disabled' : ''}`}>
                                <div className="field-header">
                                    <label>Product Type</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={fieldToggles.type}
                                            onChange={() => toggleField('type')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <select
                                    value={commonFields.type}
                                    onChange={(e) => handleCommonFieldChange('type', e.target.value)}
                                    disabled={!fieldToggles.type}
                                >
                                    <option value="">Don't change</option>
                                    <option value="simple">Simple Product</option>
                                    <option value="variable">Variable Product</option>
                                    <option value="grouped">Grouped Product</option>
                                    <option value="external">External/Affiliate Product</option>
                                </select>
                            </div>

                            {/* Status */}
                            <div className={`field-group ${!fieldToggles.status ? 'disabled' : ''}`}>
                                <div className="field-header">
                                    <label>Status</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={fieldToggles.status}
                                            onChange={() => toggleField('status')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <select
                                    value={commonFields.status}
                                    onChange={(e) => handleCommonFieldChange('status', e.target.value)}
                                    disabled={!fieldToggles.status}
                                >
                                    <option value="">Don't change</option>
                                    <option value="publish">Published</option>
                                    <option value="draft">Draft</option>
                                    <option value="private">Private</option>
                                </select>
                            </div>

                            {/* Featured */}
                            <div className={`field-group ${!fieldToggles.featured ? 'disabled' : ''}`}>
                                <div className="field-header">
                                    <label>Featured</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={fieldToggles.featured}
                                            onChange={() => toggleField('featured')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <select
                                    value={commonFields.featured}
                                    onChange={(e) => handleCommonFieldChange('featured', e.target.value === 'true')}
                                    disabled={!fieldToggles.featured}
                                >
                                    <option value="">Don't change</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            </div>

                            {/* Regular Price */}
                            <div className={`field-group ${!fieldToggles.regular_price ? 'disabled' : ''}`}>
                                <div className="field-header">
                                    <label>Regular Price</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={fieldToggles.regular_price}
                                            onChange={() => toggleField('regular_price')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <input
                                    type="number"
                                    step="0.01"
                                    placeholder="Edit individually"
                                    value={commonFields.regular_price}
                                    onChange={(e) => handleCommonFieldChange('regular_price', e.target.value)}
                                    disabled={!fieldToggles.regular_price}
                                />
                            </div>

                            {/* Sale Price */}
                            <div className={`field-group ${!fieldToggles.sale_price ? 'disabled' : ''}`}>
                                <div className="field-header">
                                    <label>Sale Price</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={fieldToggles.sale_price}
                                            onChange={() => toggleField('sale_price')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <input
                                    type="number"
                                    step="0.01"
                                    placeholder="Edit individually"
                                    value={commonFields.sale_price}
                                    onChange={(e) => handleCommonFieldChange('sale_price', e.target.value)}
                                    disabled={!fieldToggles.sale_price}
                                />
                            </div>

                            {/* Stock Status */}
                            <div className={`field-group ${!fieldToggles.stock_status ? 'disabled' : ''}`}>
                                <div className="field-header">
                                    <label>Stock Status</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={fieldToggles.stock_status}
                                            onChange={() => toggleField('stock_status')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <select
                                    value={commonFields.stock_status}
                                    onChange={(e) => handleCommonFieldChange('stock_status', e.target.value)}
                                    disabled={!fieldToggles.stock_status}
                                >
                                    <option value="">Don't change</option>
                                    <option value="instock">In Stock</option>
                                    <option value="outofstock">Out of Stock</option>
                                    <option value="onbackorder">On Backorder</option>
                                </select>
                            </div>

                            {/* Manage Stock */}
                            <div className={`field-group ${!fieldToggles.manage_stock ? 'disabled' : ''}`}>
                                <div className="field-header">
                                    <label>Manage Stock</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={fieldToggles.manage_stock}
                                            onChange={() => toggleField('manage_stock')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <select
                                    value={commonFields.manage_stock}
                                    onChange={(e) => handleCommonFieldChange('manage_stock', e.target.value === 'true')}
                                    disabled={!fieldToggles.manage_stock}
                                >
                                    <option value="">Don't change</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            </div>

                            {/* Weight */}
                            <div className={`field-group ${!fieldToggles.weight ? 'disabled' : ''}`}>
                                <div className="field-header">
                                    <label>Weight (kg)</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={fieldToggles.weight}
                                            onChange={() => toggleField('weight')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <input
                                    type="number"
                                    step="0.01"
                                    placeholder="Edit individually"
                                    value={commonFields.weight}
                                    onChange={(e) => handleCommonFieldChange('weight', e.target.value)}
                                    disabled={!fieldToggles.weight}
                                />
                            </div>

                            {/* Tax Status */}
                            <div className={`field-group ${!fieldToggles.tax_status ? 'disabled' : ''}`}>
                                <div className="field-header">
                                    <label>Tax Status</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={fieldToggles.tax_status}
                                            onChange={() => toggleField('tax_status')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <select
                                    value={commonFields.tax_status}
                                    onChange={(e) => handleCommonFieldChange('tax_status', e.target.value)}
                                    disabled={!fieldToggles.tax_status}
                                >
                                    <option value="">Don't change</option>
                                    <option value="taxable">Taxable</option>
                                    <option value="shipping">Shipping only</option>
                                    <option value="none">None</option>
                                </select>
                            </div>

                            {/* Shipping Class */}
                            <div className={`field-group ${!fieldToggles.shipping_class ? 'disabled' : ''}`}>
                                <div className="field-header">
                                    <label>Shipping Class</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={fieldToggles.shipping_class}
                                            onChange={() => toggleField('shipping_class')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <select
                                    value={commonFields.shipping_class}
                                    onChange={(e) => handleCommonFieldChange('shipping_class', e.target.value)}
                                    disabled={!fieldToggles.shipping_class}
                                >
                                    <option value="">Don't change</option>
                                    <option value="">No shipping class</option>
                                    <option value="free-shipping">Free Shipping</option>
                                    <option value="flat-rate">Flat Rate</option>
                                </select>
                            </div>

                            {/* Short Description */}
                            <div className={`field-group ${!fieldToggles.short_description ? 'disabled' : ''}`} style={{ gridColumn: '1 / -1' }}>
                                <div className="field-header">
                                    <label>Short Description</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={fieldToggles.short_description}
                                            onChange={() => toggleField('short_description')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <textarea
                                    placeholder="Edit individually"
                                    value={commonFields.short_description}
                                    onChange={(e) => handleCommonFieldChange('short_description', e.target.value)}
                                    disabled={!fieldToggles.short_description}
                                    rows="3"
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>

                            {/* Description */}
                            <div className={`field-group ${!fieldToggles.description ? 'disabled' : ''}`} style={{ gridColumn: '1 / -1' }}>
                                <div className="field-header">
                                    <label>Description</label>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={fieldToggles.description}
                                            onChange={() => toggleField('description')}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                                <textarea
                                    placeholder="Edit individually"
                                    value={commonFields.description}
                                    onChange={(e) => handleCommonFieldChange('description', e.target.value)}
                                    disabled={!fieldToggles.description}
                                    rows="5"
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>
                        </div>

                        <div className="apply-section">
                            <button 
                                className="btn btn-primary btn-lg"
                                onClick={() => applyCommonFields()}
                                disabled={saving || isOverLimit}
                            >
                                {saving ? 'Saving...' : isOverLimit ? 'Selection Limit Exceeded' : `Apply Common Fields to ${selectedProductIds.length} Selected Products`}
                            </button>
                        </div>
                    </div>
                )}

                {/* Individual Edit Mode */}
                {editMode === 'individual' && (
                    <div className="individual-edit-section">
                        <div className="section-header">
                            <h3>Edit Individual Products</h3>
                            <p>Fields with "Edit Together" OFF appear here for individual editing</p>
                        </div>

                        <div className="products-table-wrapper" ref={tableScrollRef}>
                            <table className="bulk-edit-table">
                                <thead>
                                    <tr>
                                        <th style={{width: '200px'}}>Product</th>
                                        {/* Always show these */}
                                        <th style={{width: '120px'}}>SKU</th>
                                        <th style={{width: '150px'}}>Name</th>
                                        <th style={{width: '100px'}}>Stock Qty</th>
                                        {/* Show if toggled OFF */}
                                        {!fieldToggles.type && <th style={{width: '120px'}}>Type</th>}
                                        {!fieldToggles.status && <th style={{width: '120px'}}>Status</th>}
                                        {!fieldToggles.featured && <th style={{width: '100px'}}>Featured</th>}
                                        {!fieldToggles.regular_price && <th style={{width: '100px'}}>Regular Price</th>}
                                        {!fieldToggles.sale_price && <th style={{width: '100px'}}>Sale Price</th>}
                                        {!fieldToggles.stock_status && <th style={{width: '120px'}}>Stock Status</th>}
                                        {!fieldToggles.manage_stock && <th style={{width: '120px'}}>Manage Stock</th>}
                                        {!fieldToggles.weight && <th style={{width: '100px'}}>Weight</th>}
                                        {!fieldToggles.tax_status && <th style={{width: '120px'}}>Tax Status</th>}
                                        {!fieldToggles.shipping_class && <th style={{width: '140px'}}>Shipping Class</th>}
                                        {!fieldToggles.short_description && <th style={{width: '250px'}}>Short Description</th>}
                                        {!fieldToggles.description && <th style={{width: '300px'}}>Description</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map(product => (
                                        <tr key={product.id}>
                                            <td>
                                                <div className="product-cell">
                                                    {product.images?.[0] && (
                                                        <img 
                                                            src={product.images[0].src} 
                                                            alt={product.name}
                                                            className="product-thumb"
                                                        />
                                                    )}
                                                    <div className="product-name">
                                                        {product.name}
                                                    </div>
                                                </div>
                                            </td>
                                            {/* Always show */}
                                            <td>
                                                <input
                                                    type="text"
                                                    value={productEdits[product.id]?.sku || ''}
                                                    onChange={(e) => handleProductFieldChange(product.id, 'sku', e.target.value)}
                                                    className="table-input"
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={productEdits[product.id]?.name || ''}
                                                    onChange={(e) => handleProductFieldChange(product.id, 'name', e.target.value)}
                                                    className="table-input"
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    value={productEdits[product.id]?.stock_quantity || ''}
                                                    onChange={(e) => handleProductFieldChange(product.id, 'stock_quantity', e.target.value)}
                                                    className="table-input"
                                                    disabled={!productEdits[product.id]?.manage_stock}
                                                />
                                            </td>
                                            {/* Show if toggled OFF */}
                                            {!fieldToggles.type && (
                                                <td>
                                                    <select
                                                        value={productEdits[product.id]?.type || ''}
                                                        onChange={(e) => handleProductFieldChange(product.id, 'type', e.target.value)}
                                                        className="table-select"
                                                    >
                                                        <option value="simple">Simple</option>
                                                        <option value="variable">Variable</option>
                                                        <option value="grouped">Grouped</option>
                                                        <option value="external">External</option>
                                                    </select>
                                                </td>
                                            )}
                                            {!fieldToggles.status && (
                                                <td>
                                                    <select
                                                        value={productEdits[product.id]?.status || ''}
                                                        onChange={(e) => handleProductFieldChange(product.id, 'status', e.target.value)}
                                                        className="table-select"
                                                    >
                                                        <option value="publish">Published</option>
                                                        <option value="draft">Draft</option>
                                                        <option value="private">Private</option>
                                                    </select>
                                                </td>
                                            )}
                                            {!fieldToggles.featured && (
                                                <td>
                                                    <select
                                                        value={productEdits[product.id]?.featured ? 'true' : 'false'}
                                                        onChange={(e) => handleProductFieldChange(product.id, 'featured', e.target.value === 'true')}
                                                        className="table-select"
                                                    >
                                                        <option value="false">No</option>
                                                        <option value="true">Yes</option>
                                                    </select>
                                                </td>
                                            )}
                                            {!fieldToggles.regular_price && (
                                                <td>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={productEdits[product.id]?.regular_price || ''}
                                                        onChange={(e) => handleProductFieldChange(product.id, 'regular_price', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.sale_price && (
                                                <td>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={productEdits[product.id]?.sale_price || ''}
                                                        onChange={(e) => handleProductFieldChange(product.id, 'sale_price', e.target.value)}
                                                        className="table-input"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.stock_status && (
                                                <td>
                                                    <select
                                                        value={productEdits[product.id]?.stock_status || ''}
                                                        onChange={(e) => handleProductFieldChange(product.id, 'stock_status', e.target.value)}
                                                        className="table-select"
                                                    >
                                                        <option value="instock">In Stock</option>
                                                        <option value="outofstock">Out of Stock</option>
                                                        <option value="onbackorder">On Backorder</option>
                                                    </select>
                                                </td>
                                            )}
                                            {!fieldToggles.manage_stock && (
                                                <td>
                                                    <select
                                                        value={productEdits[product.id]?.manage_stock ? 'true' : 'false'}
                                                        onChange={(e) => handleProductFieldChange(product.id, 'manage_stock', e.target.value === 'true')}
                                                        className="table-select"
                                                    >
                                                        <option value="false">No</option>
                                                        <option value="true">Yes</option>
                                                    </select>
                                                </td>
                                            )}
                                            {!fieldToggles.weight && (
                                                <td>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={productEdits[product.id]?.weight || ''}
                                                        onChange={(e) => handleProductFieldChange(product.id, 'weight', e.target.value)}
                                                        className="table-input"
                                                        placeholder="kg"
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.tax_status && (
                                                <td>
                                                    <select
                                                        value={productEdits[product.id]?.tax_status || ''}
                                                        onChange={(e) => handleProductFieldChange(product.id, 'tax_status', e.target.value)}
                                                        className="table-select"
                                                    >
                                                        <option value="taxable">Taxable</option>
                                                        <option value="shipping">Shipping only</option>
                                                        <option value="none">None</option>
                                                    </select>
                                                </td>
                                            )}
                                            {!fieldToggles.shipping_class && (
                                                <td>
                                                    <select
                                                        value={productEdits[product.id]?.shipping_class || ''}
                                                        onChange={(e) => handleProductFieldChange(product.id, 'shipping_class', e.target.value)}
                                                        className="table-select"
                                                    >
                                                        <option value="">No shipping class</option>
                                                        <option value="free-shipping">Free Shipping</option>
                                                        <option value="flat-rate">Flat Rate</option>
                                                    </select>
                                                </td>
                                            )}
                                            {!fieldToggles.short_description && (
                                                <td>
                                                    <textarea
                                                        value={productEdits[product.id]?.short_description || ''}
                                                        onChange={(e) => handleProductFieldChange(product.id, 'short_description', e.target.value)}
                                                        className="table-input"
                                                        rows="2"
                                                        style={{resize: 'vertical', minHeight: '40px'}}
                                                    />
                                                </td>
                                            )}
                                            {!fieldToggles.description && (
                                                <td>
                                                    <textarea
                                                        value={productEdits[product.id]?.description || ''}
                                                        onChange={(e) => handleProductFieldChange(product.id, 'description', e.target.value)}
                                                        className="table-input"
                                                        rows="3"
                                                        style={{resize: 'vertical', minHeight: '60px'}}
                                                    />
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        
                        <div className="apply-section">
                            <button 
                                className="btn btn-primary btn-lg"
                                onClick={() => applyIndividualChanges()}
                                disabled={saving || isOverLimit}
                            >
                                {saving ? 'Saving...' : isOverLimit ? 'Selection Limit Exceeded' : `Apply Individual Changes to ${selectedProductIds.length} Products`}
                            </button>
                        </div>
                    </div>
                )}

                {/* Summary */}
                <div className="bulk-edit-summary">
                    <div className="summary-card">
                        <h4>Summary</h4>
                        <div className="summary-stats">
                            <div className="stat">
                                <span className="stat-label">Products Selected:</span>
                                <span className="stat-value">{products.length}</span>
                            </div>
                            <div className="stat">
                                <span className="stat-label">Mode:</span>
                                <span className="stat-value">{editMode === 'common' ? 'Common Fields' : 'Individual Edit'}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Batch Loading Modal */}
            <BatchProgressModal
                isOpen={showLoadingModal}
                onClose={() => {
                    setShowLoadingModal(false);
                    resetLoadingProgress();
                }}
                progress={loadingProgress}
                currentBatch={loadingProgress.currentBatch}
                totalBatches={loadingProgress.totalBatches}
                processedItems={loadingProgress.loadedItems}
                totalItems={loadingProgress.totalItems}
                operationType="products"
                isCompleted={loadingProgress.isCompleted}
                error={loadingProgress.error}
                isLoading={true}
            />

            {/* Batch Progress Modal */}
            <BatchProgressModal
                isOpen={showBatchModal}
                onClose={() => {
                    setShowBatchModal(false);
                    resetProgress();
                }}
                progress={batchProgress}
                currentBatch={batchProgress.currentBatch}
                totalBatches={batchProgress.totalBatches}
                processedItems={batchProgress.processedItems}
                totalItems={batchProgress.totalItems}
                operationType="products"
                isCompleted={batchProgress.isCompleted}
                error={batchProgress.error}
            />
        </div>
    );
};

export default BulkProductEditor;
